import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, CheckCheck, Inbox, MessageSquare, BadgeCheck, CalendarClock, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICON = { inquiry: MessageSquare, lead: MessageSquare, approval: BadgeCheck, renewal: CalendarClock, expired: AlertTriangle };

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const seen = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items); setUnread(data.unread);
      const newest = data.items[0]?.id;
      if (seen.current && newest && newest !== seen.current && data.items[0] && !data.items[0].read) {
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(data.items[0].title, { body: data.items[0].body, icon: "/logo-mark.png" });
        }
      }
      seen.current = newest || "none";
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 20000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, [user]);

  if (!user) return null;

  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  const askPush = () => { if ("Notification" in window && Notification.permission === "default") Notification.requestPermission(); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen(!open); askPush(); }} className="relative w-10 h-10 rounded-full border border-[color:var(--gold)]/70 bg-white flex items-center justify-center text-[color:var(--maroon)] hover:bg-[color:var(--maroon)] hover:text-[color:var(--cream-primary)] transition-colors" data-testid="notif-bell" aria-label="Notifications">
        <Bell className="w-4 h-4"/>
        {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--gold)] text-[color:var(--maroon-2)] text-[10px] font-bold flex items-center justify-center" data-testid="notif-unread">{unread > 9 ? "9+" : unread}</span>}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="absolute right-0 mt-2 w-[340px] max-w-[90vw] bg-white rounded-2xl shadow-2xl border border-[color:var(--line)] overflow-hidden z-50" data-testid="notif-panel">
            <div className="px-4 py-3 flex items-center justify-between border-b border-[color:var(--line)] bg-[color:var(--ivory)]">
              <div className="font-display text-lg text-[color:var(--maroon)]">Notifications</div>
              <button onClick={readAll} className="text-[11px] inline-flex items-center gap-1 text-[color:var(--maroon)] hover:underline" data-testid="notif-read-all"><CheckCheck className="w-3.5 h-3.5"/> Mark all read</button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.map((n) => { const I = ICON[n.type] || Inbox; return (
                <button key={n.id} onClick={async () => { if (!n.read) { await api.post(`/notifications/${n.id}/read`); load(); } }} className={`w-full text-left px-4 py-3 flex gap-3 border-b border-[color:var(--line)]/60 hover:bg-[color:var(--ivory)] ${n.read ? "opacity-70" : ""}`} data-testid={`notif-item-${n.id}`}>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.type === "expired" ? "bg-rose-50 text-rose-700" : "bg-[color:var(--ivory)] text-[color:var(--maroon)]"}`}><I className="w-4 h-4"/></span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[color:var(--ink)] leading-tight">{n.title}</span>
                    <span className="block text-xs text-neutral-600 mt-0.5 leading-snug">{n.body}</span>
                    <span className="block text-[10px] text-neutral-400 mt-1">{new Date(n.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                  </span>
                  {!n.read && <span className="ml-auto mt-1 w-2 h-2 rounded-full bg-[color:var(--gold)] shrink-0"/>}
                </button>
              ); })}
              {!items.length && <div className="px-4 py-12 text-center text-sm text-neutral-500">You're all caught up.</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
